import { AGENT_CATALOG, agentById, type AgentCatalogEntry } from "~/lib/siteglue/business-ready/agent-catalog";
import type { AgentId } from "~/lib/siteglue/business-ready/types";

export type LiveWalkConfig = {
  agentId: AgentId;
  model: string;
  runtime: string;
  modeLabel: string;
  restriction: string;
};

export const FEATURED_WALK_AGENTS: AgentId[] = ["claude", "grok", "muse", "openclaw"];

const LIVE_WALK_CONFIG: Partial<Record<AgentId, LiveWalkConfig>> = {
  claude: {
    agentId: "claude",
    model: "claude-sonnet-4-6",
    runtime: "anthropic",
    modeLabel: "Claude Duet model on SiteGlue",
    restriction: "Claude Duet model on SiteGlue. This is not the Claude consumer app.",
  },
  grok: {
    agentId: "grok",
    model: "grok-2-latest",
    runtime: "xai",
    modeLabel: "Grok model on SiteGlue",
    restriction: "Grok model on SiteGlue. This is not the Grok consumer app.",
  },
};

export function liveWalkConfig(agentId: string | undefined): LiveWalkConfig | undefined {
  if (!agentId) return LIVE_WALK_CONFIG.claude;
  return LIVE_WALK_CONFIG[agentId as AgentId];
}

export function canRunLiveModelWalk(agentId: string | undefined): boolean {
  return Boolean(liveWalkConfig(agentId));
}

/** Server-only: live walk needs a model key. Missing keys fall back to from_scan. */
export function canExecuteLiveModelWalk(agentId: string | undefined): boolean {
  const config = liveWalkConfig(agentId);
  if (!config) return false;
  if (config.runtime === "anthropic") return Boolean(process.env.ANTHROPIC_API_KEY?.trim());
  if (config.runtime === "xai") return Boolean(process.env.XAI_API_KEY?.trim());
  return false;
}

export function walkAgentNoun(agentId: string | undefined): string {
  const agent = agentById(agentId);
  if (!agent || agent.id === "claude") return "Claude Duet";
  if (agent.id === "grok") return "Grok Bot";
  return `${agent.label} AI Agent`;
}

export function walkAgentsForPicker(selected?: AgentId[] | null): AgentCatalogEntry[] {
  const ids = new Set<AgentId>(FEATURED_WALK_AGENTS);
  for (const id of selected ?? []) ids.add(id);
  return AGENT_CATALOG.filter((agent) => ids.has(agent.id));
}

export function displayWalkInsight(insight: string, agentId?: string): string {
  const noun = walkAgentNoun(agentId);
  return insight.replace(/\bThe visitor\b/g, `The ${noun}`).replace(/\bthe visitor\b/g, `the ${noun}`);
}

export function insightVoiceInstruction(agentId?: string): string {
  const noun = walkAgentNoun(agentId);
  return `Write as the ${noun}, never as a visitor. Example: "The ${noun} arrived at the homepage and opened the family-law page."`;
}

export function tokensFromUsage(usage: unknown): number | undefined {
  if (!usage || typeof usage !== "object") return undefined;
  const value = usage as { totalTokens?: unknown; promptTokens?: unknown; completionTokens?: unknown };
  if (typeof value.totalTokens === "number" && value.totalTokens > 0) return Math.round(value.totalTokens);
  const prompt = typeof value.promptTokens === "number" ? value.promptTokens : 0;
  const completion = typeof value.completionTokens === "number" ? value.completionTokens : 0;
  const total = prompt + completion;
  return total > 0 ? Math.round(total) : undefined;
}
